import React, { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Grid } from '@xsolla/ui-core';

import { StorageTypes } from '../../../../tests/mocks/utils';
import { useCurrentProject } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { getRandomString, isPasskeyAvailable } from '../../../utils';
import {
  EmailValidateIcon,
  EnterpriseIcon,
  MiscellaneousIcon,
  PasskeyIcon,
  SocialIcon,
  ToggleBox
} from '../../ui';
import NavigationSettingsLink from '../NavigationSettingsLink';

const AuthenticationSettings = (): JSX.Element | null => {
  const {
    enterpriseConnectionsPath,
    miscellaneousPath,
    passkeyPath,
    platformSettingsPath,
    socialConnectionPath
  } = usePaths();
  const intl = useIntl();
  const { currentProject } = useCurrentProject();

  const isSocialLogin =
    !currentProject?.authentication_allowed &&
    !currentProject?.is_email_auth_allowed &&
    !currentProject?.is_phone_auth_allowed;

  const isXsollaOrCustomStorage =
    currentProject?.users_storage === StorageTypes.XSOLLA ||
    currentProject?.users_storage === StorageTypes.CUSTOM;

  const isPasskeyShown = isPasskeyAvailable(currentProject);

  const renderNavSettings: ReactElement[] = useMemo(() => {
    const result = [];

    if (!isSocialLogin) {
      result.push(
        <NavigationSettingsLink
          dataTestId="link-navigation-social-settings"
          icon={<SocialIcon />}
          titleInfo={{
            label: intl.messages['navigation.settings.authentication.social-connections.title'],
            path: socialConnectionPath(currentProject.id)
          }}
          subtitle={intl.messages['navigation.settings.authentication.social-connections.subtitle']}
        />
      );
    }

    if (isXsollaOrCustomStorage) {
      result.push(
        <NavigationSettingsLink
          dataTestId="link-navigation-social-cross"
          icon={<EmailValidateIcon />}
          titleInfo={{
            label: intl.messages['navigation.settings.authentication.publisher-platforms.title'],
            path: platformSettingsPath(currentProject?.id as string)
          }}
          subtitle={intl.messages['social-connection.subtitle']}
        />
      );
    }

    result.push(
      <NavigationSettingsLink
        dataTestId="link-navigation-enterprise-connections"
        icon={<EnterpriseIcon />}
        titleInfo={{
          label: intl.messages['navigation.settings.authentication.enterprise-connections.title'],
          path: enterpriseConnectionsPath(currentProject?.id as string)
        }}
        subtitle={
          intl.messages['navigation.settings.authentication.enterprise-connections.subtitle']
        }
      />
    );

    if (isPasskeyShown) {
      result.push(
        <NavigationSettingsLink
          dataTestId="link-navigation-passkey"
          icon={<PasskeyIcon />}
          titleInfo={{
            label: intl.messages['navigation.settings.authentication.passkey.title'],
            path: passkeyPath(currentProject?.id as string)
          }}
          subtitle={intl.messages['navigation.settings.authentication.passkey.subtitle']}
        />
      );
    }

    result.push(
      <NavigationSettingsLink
        dataTestId="link-navigation-miscellaneous"
        icon={<MiscellaneousIcon />}
        titleInfo={{
          label: intl.messages['navigation.settings.authentication.miscellaneous-data.title'],
          path: miscellaneousPath(currentProject?.id as string)
        }}
        subtitle={intl.messages['navigation.settings.authentication.miscellaneous-data.subtitle']}
      />
    );

    return result;
  }, [isXsollaOrCustomStorage, isSocialLogin, isPasskeyShown]);

  return (
    <>
      <ToggleBox
        title={intl.messages['navigation.settings.authentication.label']}
        openByDefault
        dataTestId="navigation-block-authentication">
        <Grid cols={2} gap={24}>
          {renderNavSettings.map((elem) => (
            <div key={getRandomString()}>{elem}</div>
          ))}
        </Grid>
      </ToggleBox>
    </>
  );
};

export default AuthenticationSettings;
